import { Avatar, Box, Button, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import api from '../../../service/api'
import { formatDate, Icons } from '../../../utils/utilities'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import noDiscussionsImg from '../../../assets/no-discussions.svg'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import AuditDiscussionViewer from '../discussions/AuditDiscussionViewer'
import CodeButton from '../../common/CodeButton'
import CustomChip from '../../common/CustomChip'


const DiscussionDetails = ( { userData } ) => {

    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [discussions, setDiscussions] = useState( [] )
    const [selectedDiscussion, setSelectedDiscussion] = useState( null )

    const { palette, border, layout } = useTheme()

    const { empcode } = useParams()
    const [searchParams] = useSearchParams( { legacy: true } )
    const legacy = searchParams.get( 'legacy' ) === 'true'

    const handleDialogClose = () => {
        setSelectedDiscussion( null )
    }

    const loadDiscussions = useCallback( async () => {
        setLoading( true )
        try {
            const { data: { data } } = await api.audit.getDiscussionsByFacultyId( empcode, { legacy } )
            setDiscussions( data )
            setError( false )
        } catch ( err ) {
            setError( err?.response?.data?.message || "Something went wrong while fetching discussions details." )
        } finally {
            setLoading( false )
        }
    }, [empcode, legacy] )

    useEffect( () => {
        loadDiscussions()
    }, [loadDiscussions] )

    return (
        <Box maxHeight="350px" display="flex" flexDirection="column" overflow="hidden">

            {selectedDiscussion && <AuditDiscussionViewer discussion={selectedDiscussion} handleClose={handleDialogClose} openState={Boolean( selectedDiscussion )} />}

            {!loading && !error && discussions.length > 0 && <Box display="flex" flexDirection="column" gap="10px" borderRadius={layout.borderRadius} overflow="auto" className="custom-scrollbar">
                {discussions.map( ( row, index ) => {
                    return <Box sx={{ cursor: "pointer" }} onClick={() => setSelectedDiscussion( row )} borderBottom={border[1]} padding="10px" bgcolor={palette.contentBg} borderRadius={layout.borderRadius} display="flex" gap="20px" key={row._id}>
                        <Box>
                            <Tooltip title={row.creator_data.name}>
                                <Avatar src={row.creator_data?.user_profile_image_url} >{row.creator_data.name[0]}</Avatar>
                            </Tooltip>
                        </Box>
                        <Box>
                            <Box marginTop="5px" display="flex" flexWrap="wrap" alignItems="center" gap="10px">
                                {row.access_groups?.map( ag => (
                                    <CustomChip value={ag.subject_name} key={ag.subject_id} />
                                ) )}
                            </Box>
                            <Typography variant='h6' fontSize="16px">{row.title}</Typography>
                            <Typography variant='subtitle2' fontSize="12px"> On: {formatDate( row.created_at, { month: "short" } )}</Typography>
                            <Box marginTop="5px" display="flex" flexWrap="wrap" alignItems="center" gap="10px">
                                {row.categories?.map( cat => (
                                    <CustomChip variant='filled' value={cat} key={cat} />
                                ) )}
                            </Box>
                        </Box>
                    </Box>
                } )}</Box>}
            {loading && !error && <Box padding="50px 20px" display="flex" justifyContent="center">
                <DataLoadingSpinner waitingMessage="Please wait! Loading discussions..." />
            </Box>}
            {error && <TryAgainMessageBlock getData={loadDiscussions} loading={loading} err={error} />}
            {!loading && !error && discussions.length === 0 && <Box bgcolor={palette.contentBg} borderRadius={layout.borderRadius} padding="20px" gap="20px" display="flex" alignItems="center" flexDirection="column" justifyContent="center">
                <img src={noDiscussionsImg} height="100px" alt="Something went wrong!" />
                <Typography variant="subtitle2"><em>{userData.employee_name}</em> hasn't created any discussions!</Typography>
                <Button disabled={loading} onClick={loadDiscussions} startIcon={Icons.default.Replay} variant="outlined" sx={{ textTransform: "capitalize" }} color="secondary">Fetch again</Button>
            </Box>}
        </Box>
    )
}

export default DiscussionDetails
